import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];
const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safe = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const arr = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const errText = (e, fallback) => {
  const d = e?.response?.data;
  if (typeof d === "string" && d.trim()) return d;
  if (typeof d?.message === "string" && d.message.trim()) return d.message;
  if (typeof d?.title === "string" && d.title.trim()) return d.title;
  if (typeof e?.message === "string" && e.message.trim()) return e.message;
  return fallback;
};

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    background: "#fff",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
  },
  header: {
    background: "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 800, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  section: {
    fontSize: 11,
    fontWeight: 800,
    color: "#0f766e",
    borderBottom: "1px solid #e9ecef",
    paddingBottom: 4,
    margin: "8px 0 6px",
  },
  tableWrap: {
    maxHeight: 430,
    overflow: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 1200, marginBottom: 0 },
  th: {
    fontSize: 10,
    color: "#fff",
    background: "#07111f",
    padding: "7px 8px",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
  },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function AdvanceVoucherPage() {
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    advanceNo: "",
    advanceDate: today(),
    advanceType: "RECEIPT",
    partyType: "CUSTOMER",
    partyId: "",
    mode: "CASH",
    cashBankAccountId: "",
    amount: "",
    referenceNo: "",
    notes: "",
  });

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.allSettled([loadMasters(), loadRows(), loadNextNo("RECEIPT")]);
    setLoading(false);
  };

  const parties = useMemo(
    () => (form.partyType === "SUPPLIER" ? suppliers : customers),
    [form.partyType, suppliers, customers]
  );

  const cashBankAccounts = useMemo(() => {
    const mode = form.mode?.toUpperCase();

    return accounts.filter((a) => {
      const name = safe(val(a, "AccountName", "accountName")).toLowerCase();
      const code = safe(val(a, "AccountCode", "accountCode")).toLowerCase();

      if (mode === "CASH") return name.includes("cash") || code.startsWith("1100");
      if (mode === "BANK" || mode === "UPI")
        return name.includes("bank") || name.includes("upi") || code.startsWith("1200");

      return true;
    });
  }, [accounts, form.mode]);

  const totalReceived = useMemo(
    () =>
      rows
        .filter((x) => safe(val(x, "AdvanceType", "advanceType")).toUpperCase() === "RECEIPT")
        .reduce((s, x) => s + num(val(x, "Amount", "amount")), 0),
    [rows]
  );

  const totalPaid = useMemo(
    () =>
      rows
        .filter((x) => safe(val(x, "AdvanceType", "advanceType")).toUpperCase() === "PAYMENT")
        .reduce((s, x) => s + num(val(x, "Amount", "amount")), 0),
    [rows]
  );

  const loadMasters = async () => {
    try {
      const cid = companyId();
      const params = cid ? { companyId: cid } : {};

      const [c, s, a] = await Promise.all([
        client.get("/sales/customers", { params }),
        client.get("/suppliers", { params }),
        client.get("/accounts", { params }),
      ]);

      setCustomers(arr(c.data));
      setSuppliers(arr(s.data));
      setAccounts(arr(a.data));
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to load masters"));
    }
  };

  const loadNextNo = async (advanceType = form.advanceType) => {
    try {
      const res = await client.get("/advance-vouchers/next-no", {
        params: {
          companyId: companyId() || undefined,
          advanceType,
        },
      });

      const nextNo = safe(val(res.data, "AdvanceNo", "advanceNo", "voucherNo", "VoucherNo"));

      setForm((f) => ({
        ...f,
        advanceNo:
          nextNo ||
          (advanceType === "PAYMENT"
            ? `ADP-${Date.now().toString().slice(-5)}`
            : `ADR-${Date.now().toString().slice(-5)}`),
      }));
    } catch {
      setForm((f) => ({
        ...f,
        advanceNo:
          advanceType === "PAYMENT"
            ? `ADP-${Date.now().toString().slice(-5)}`
            : `ADR-${Date.now().toString().slice(-5)}`,
      }));
    }
  };

  const loadRows = async () => {
    try {
      const res = await client.get("/advance-vouchers", {
        params: companyId() ? { companyId: companyId() } : {},
      });

      setRows(arr(res.data));
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to load advance vouchers"));
    }
  };

  const changeAdvanceType = async (advanceType) => {
    const partyType = advanceType === "PAYMENT" ? "SUPPLIER" : "CUSTOMER";

    setForm((f) => ({
      ...f,
      advanceType,
      partyType,
      partyId: "",
      advanceNo: "",
    }));

    await loadNextNo(advanceType);
  };

  const changeMode = (mode) => {
    setForm((f) => ({
      ...f,
      mode,
      cashBankAccountId: "",
    }));
  };

  const save = async () => {
    if (!safe(form.advanceNo).trim()) return alert("Advance No is required");
    if (!form.partyId) return alert("Party is required");
    if (!form.cashBankAccountId) return alert("Cash/Bank account is required");
    if (num(form.amount) <= 0) return alert("Amount must be greater than zero");

    try {
      setSaving(true);

      await client.post("/advance-vouchers", {
        companyId: companyId(),
        advanceNo: safe(form.advanceNo).trim(),
        advanceDate: form.advanceDate,
        advanceType: form.advanceType,
        partyType: form.partyType,
        partyId: Number(form.partyId),
        mode: form.mode,
        cashBankAccountId: Number(form.cashBankAccountId),
        amount: num(form.amount),
        referenceNo: safe(form.referenceNo).trim(),
        notes: safe(form.notes).trim(),
      });

      alert("Advance voucher saved successfully");

      const currentType = form.advanceType;

      setForm({
        advanceNo: "",
        advanceDate: today(),
        advanceType: currentType,
        partyType: currentType === "PAYMENT" ? "SUPPLIER" : "CUSTOMER",
        partyId: "",
        mode: "CASH",
        cashBankAccountId: "",
        amount: "",
        referenceNo: "",
        notes: "",
      });

      await loadNextNo(currentType);
      await loadRows();
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to save advance voucher"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-wallet2 me-2"></i>
            Advance Receipt / Advance Payment
          </h6>
          <div style={styles.sub}>
            Record customer advances and supplier advances before invoice/bill adjustment
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <Stat title="Advance Received" value={`₹ ${money(totalReceived)}`} />
            <Stat title="Advance Paid" value={`₹ ${money(totalPaid)}`} />
            <Stat title="Net Advance" value={`₹ ${money(totalReceived - totalPaid)}`} />
            <Stat title="Open Vouchers" value={rows.length} />
          </div>

          <div style={styles.section}>Advance Voucher Entry</div>

          <div className="row g-2 mb-2">
            <Field label="Advance Type">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.advanceType}
                onChange={(e) => changeAdvanceType(e.target.value)}
              >
                <option value="RECEIPT">Advance Receipt</option>
                <option value="PAYMENT">Advance Payment</option>
              </select>
            </Field>

            <Field label="Advance No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.advanceNo}
                onChange={(e) => setForm({ ...form, advanceNo: e.target.value })}
              />
            </Field>

            <Field label="Date">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.advanceDate}
                onChange={(e) => setForm({ ...form, advanceDate: e.target.value })}
              />
            </Field>

            <Field label="Party Type">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.partyType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    partyType: e.target.value,
                    partyId: "",
                  })
                }
              >
                <option value="CUSTOMER">CUSTOMER</option>
                <option value="SUPPLIER">SUPPLIER</option>
              </select>
            </Field>

            <Field label="Party" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.partyId}
                onChange={(e) => setForm({ ...form, partyId: e.target.value })}
              >
                <option value="">Select Party</option>
                {parties.map((p) => {
                  const id = val(p, "Id", "id");
                  const name =
                    form.partyType === "SUPPLIER"
                      ? safe(val(p, "SupplierName", "supplierName", "Name", "name"))
                      : safe(val(p, "CustomerName", "customerName", "Name", "name"));

                  return (
                    <option key={id} value={id}>
                      {name || `Party #${id}`}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Mode">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.mode}
                onChange={(e) => changeMode(e.target.value)}
              >
                <option value="CASH">CASH</option>
                <option value="BANK">BANK</option>
                <option value="UPI">UPI</option>
              </select>
            </Field>

            <Field label="Cash / Bank A/c" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.cashBankAccountId}
                onChange={(e) =>
                  setForm({ ...form, cashBankAccountId: e.target.value })
                }
              >
                <option value="">Select Account</option>
                {cashBankAccounts.map((a) => {
                  const id = val(a, "Id", "id");
                  return (
                    <option key={id} value={id}>
                      {safe(val(a, "AccountCode", "accountCode"))} -{" "}
                      {safe(val(a, "AccountName", "accountName"))}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Amount">
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Field>

            <Field label="Reference No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.referenceNo}
                onChange={(e) => setForm({ ...form, referenceNo: e.target.value })}
              />
            </Field>

            <Field label="Notes" col="col-md-4">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>

          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              style={{ ...styles.btn, minWidth: 100 }}
              onClick={() => window.print()}
            >
              Print
            </button>

            <button
              type="button"
              className="btn btn-success btn-sm"
              style={{ ...styles.btn, minWidth: 170 }}
              onClick={save}
              disabled={saving || loading}
            >
              {saving ? "Saving..." : "Save Advance Voucher"}
            </button>
          </div>

          <div style={styles.section}>Advance Voucher Register</div>

          <div style={styles.tableWrap}>
            <table className="table table-bordered table-hover align-middle" style={styles.table}>
              <thead>
                <tr>
                  {[
                    "Advance No",
                    "Date",
                    "Type",
                    "Party Type",
                    "Party",
                    "Mode",
                    "Cash/Bank",
                    "Amount",
                    "Adjusted",
                    "Balance",
                    "Status",
                    "Reference",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={["Amount", "Adjusted", "Balance"].includes(h) ? "text-end" : ""}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center text-muted" style={{ padding: 18 }}>
                      {loading ? "Loading..." : "No advance vouchers found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => {
                    const id = val(r, "Id", "id") || index;

                    return (
                      <tr key={id}>
                        <td style={{ ...styles.td, fontWeight: 800 }}>
                          {safe(val(r, "AdvanceNo", "advanceNo"))}
                        </td>
                        <td style={styles.td}>
                          {safe(val(r, "AdvanceDate", "advanceDate")).slice(0, 10)}
                        </td>
                        <td style={styles.td}>{safe(val(r, "AdvanceType", "advanceType"))}</td>
                        <td style={styles.td}>{safe(val(r, "PartyType", "partyType"))}</td>
                        <td style={styles.td}>{safe(val(r, "PartyName", "partyName"))}</td>
                        <td style={styles.td}>{safe(val(r, "Mode", "mode"))}</td>
                        <td style={styles.td}>
                          {safe(val(r, "CashBankAccount", "cashBankAccount", "CashBankAccountName", "cashBankAccountName"))}
                        </td>
                        <td className="text-end" style={styles.td}>
                          {money(val(r, "Amount", "amount"))}
                        </td>
                        <td className="text-end" style={styles.td}>
                          {money(val(r, "AdjustedAmount", "adjustedAmount"))}
                        </td>
                        <td className="text-end" style={styles.td}>
                          {money(val(r, "BalanceAmount", "balanceAmount"))}
                        </td>
                        <td style={styles.td}>{safe(val(r, "Status", "status")) || "OPEN"}</td>
                        <td style={styles.td}>
                          {safe(val(r, "ReferenceNo", "referenceNo")) || "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>
        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {value}
        </div>
      </div>
    </div>
  );
}